import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import {
	AcceptDeclineIconButtons,
	Button,
	CollapsibleBlock,
	Confirm,
	DynamicIconWrapper,
	Icons,
	Modal,
	Select,
	TextClamp,
	Tooltip,
} from '@components/common';
import Actions from '@components/common/Actions/Actions';
import AcquireAssetForm from '@components/ModalComponents/AcquireAssetForm';
import AssetForm from '@components/ModalComponents/AssetForm';
import AssetParamInfo from '@components/ModalComponents/AssetParamInfo';
import OrganisationInfo from '@components/ModalComponents/OrganisationInfo';
import QueryAssetForm from '@components/ModalComponents/QueryAssetForm';

import { useMutateUpdateAsset } from '@hooks/assets/useMutateUpdateAsset';
import { useLinkAndVerifySites } from '@hooks/useLinkAndVerifySite';
import { useQueryUser } from '@hooks/useQueryUser';
import { useSiteProfile } from '@hooks/useSiteProfile';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { isEmpty } from '@utils/common';
import { formatDate } from '@utils/date';
import { optionsFormattedForSites } from '@utils/formatter';
import { toggleCollapsibleBlockMapInfo } from '@utils/toggleCollapsibleBlockMapInfo';
import { getAssetExtraInfo } from './helpers/normalizeAssetsData';

import { TAssetProfile } from '@interfaces/assets/asset';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IRentInfoItem } from '@interfaces/rent/assetRent';
import { IExtraInfoModal, ISingleAssetInfoProps } from './interfaces';

import { EOrganizationType } from '@enums/common';
import { EAssetType } from '@enums/map';
import { EParamTypesExtraInfo } from '@components/Map/AssetsInfo/enums';

import { DEFAULT_CLUSTER_ZOOM_LVL } from '@constants/common';
import { MODAL_WIDTH_580 } from '@constants/modals';
import { routes } from '@constants/routes';
import {
	siteCantBeLinkedWarning,
	siteOutOfRadiusText,
	updateCoordsAndLinkText,
	updateCoordsText,
} from '@components/routes/AdminPanel/ManageSites/constants';

import Parameter from './parts/Parameter';

import commonStyles from '@styles/common.module.scss';

import styles from './SelectedAssetInfo.module.scss';

const collapsibleBlocksInitialState = {
	addressInfo: false,
	assetAttributes: false,
	assetRentInfo: false,
	telecomSiteInfo: false,
};

const linkTooltipText = 'Link Telecom Site you own or acquired to the Asset';
const unlinkTooltipText = 'Unlink Telecom Site you own or acquired to the Asset';

const { adminPanel } = routes;

const SingleAssetInfo = ({
	asset,
	isLoading,
	panTo,
}: ISingleAssetInfoProps) => {
	const assetType = asset?.assetType || '';

	const [
		collapsibleBlocks,
		setCollapsibleBlocks,
	] = useState<Record<string, boolean>>(() => ({
		...collapsibleBlocksInitialState,
		assetAttributes: true,
	}));

	const { accessPermission } = useAppSelector(auth);
	const navigate = useNavigate();

	const {
		assetOwnerAttributeUpdater,
		assetOwnerContact,
		assetOwnerDefaultContact,
		assetOwnerOrganizationAdministrator,
		assetOwnerSignatory,
		assetOwnerViewer,
		assetOwnerManageRequests,
		assetOwnerApprovalUnit,
		networkOperatorAssetEnquirer,
		networkOperatorAssetViewer,
		networkOperatorOrganizationAdministrator,
		networkOperatorTelecomSitesAttributeUpdater,
		assetConsumerOrganizationAdministrator,
		assetConsumerAssetEnquirer,
		assetConsumerAssetViewer,
	} = accessPermission;

	const { organisationId, organisationTypes } = useQueryUser();

	const isAssetOwner = organisationId === asset?.owner?.id;
	const areAssetFormsAvailable = networkOperatorAssetEnquirer || assetConsumerAssetEnquirer;
	const isAssetConsumer = organisationTypes.some(type => type.name === EOrganizationType.assetConsumer);

	const isMNOInfoAvailable = (
		networkOperatorAssetEnquirer ||
		networkOperatorOrganizationAdministrator ||
		networkOperatorAssetViewer ||
		assetConsumerOrganizationAdministrator ||
		assetConsumerAssetEnquirer ||
		assetConsumerAssetViewer
	);

	const isEditAssetAvailable = (
		assetOwnerOrganizationAdministrator ||
		assetOwnerAttributeUpdater
	) && isAssetOwner;

	const isViewAssetOnAdminPanelAvailable =
		isAssetOwner && (
			assetOwnerOrganizationAdministrator ||
			assetOwnerViewer ||
			assetOwnerContact ||
			assetOwnerDefaultContact ||
			assetOwnerSignatory ||
			assetOwnerAttributeUpdater ||
			assetOwnerManageRequests ||
			assetOwnerApprovalUnit
		) && !(
			networkOperatorTelecomSitesAttributeUpdater &&
			assetOwnerAttributeUpdater &&
			!isAssetOwner
		);

	const isAssetOwnerFieldHidden = (
			assetOwnerContact ||
			assetOwnerSignatory ||
			assetOwnerContact ||
			assetOwnerViewer ||
			assetOwnerDefaultContact ||
			assetOwnerAttributeUpdater
		)
		&& (
			!networkOperatorAssetViewer &&
			!networkOperatorAssetEnquirer &&
			!networkOperatorOrganizationAdministrator &&
			!networkOperatorTelecomSitesAttributeUpdater &&
			!assetConsumerOrganizationAdministrator &&
			!assetConsumerAssetEnquirer &&
			!assetConsumerAssetViewer
		);

	const isLinkAccess = networkOperatorTelecomSitesAttributeUpdater
		&& (networkOperatorAssetEnquirer || assetConsumerAssetEnquirer);
	const isEditLinkAccess = (
		networkOperatorAssetEnquirer
		|| networkOperatorOrganizationAdministrator
		|| assetConsumerOrganizationAdministrator
		|| assetConsumerAssetEnquirer
	);
	const isAcquiredLabelAvailable = networkOperatorAssetEnquirer
		|| networkOperatorTelecomSitesAttributeUpdater
		|| assetConsumerAssetEnquirer;

	const [isQueryAssetForm, setIsQueryAssetForm] = useState(false);
	const [isAcquireAssetForm, setIsAcquireAssetForm] = useState(false);
	const [isExtraInfoOpen, setIsExtraInfoOpen] = useState(false);
	const [isOrganisationInfoWindow, setIsOrganisationInfoWindow] = useState(false);
	const [isEditAssetForm, setIsEditAssetForm] = useState(false);
	const [isAssetFormDirty, setIsAssetFormDirty] = useState(false);
	const [isAcquireFormDirty, setIsAcquireFormDirty] = useState(false);
	const [isQueryFormDirty, setIsQueryFormDirty] = useState(false);
	const [isLinkMenu, setLinkMenu] = useState(false);
	const [isUnlinkConfirm, setIsUnlinkConfirm] = useState(false);
	const [isRewriteCoordConfirm, setIsRewriteCoordConfirm] = useState(false);
	const [isEditLinkMode, setIsEditLinkMode] = useState(false);
	const [isEditConfirm, setIsEditConfirm] = useState(false);

	const [selectedSiteId, setSelectedSiteId] = useState<TSelectOption<string>>();
	const [linkedSite, setLinkedSite] = useState<TSelectOption<string>>();

	const isAddressAvailable = (
		([EAssetType.building, EAssetType.land] as any).includes(assetType.assetTypeName) ||
		!!asset.locationDescription
	);
	const lastLinkedSite = asset.rentInfo?.find((rentInfoItem: IRentInfoItem) => {
		return !!rentInfoItem.site;
	})?.site;
	const isLinked = !!lastLinkedSite;
	const isRented = !!asset.rentInfo?.length;
	const isViewAssetOnAdminForRentedAssets = (networkOperatorAssetEnquirer || assetConsumerAssetEnquirer) && isRented;

	const [extraInfo, setExtraInfo] = useState<IExtraInfoModal | null>(null);

	const clearLinkState = () => {
		setLinkMenu(false);
		setSelectedSiteId(undefined);
		setIsEditLinkMode(false);
	};

	const closeUpdateSiteModal = () => {
		setIsRewriteCoordConfirm(false);
		clearLinkState();
	};
	const toggleUpdateSiteModal = () => setIsRewriteCoordConfirm(prev => !prev);
	const toggleShowMenu = () => setLinkMenu((prev) => !prev);
	const closeLinkMenu = () => clearLinkState();
	const openUnlinkConfirm = () => setIsUnlinkConfirm(true);
	const closeUnlinkConfirm = () => setIsUnlinkConfirm(false);
	const toggleOrganisationInfo = () => setIsOrganisationInfoWindow(!isOrganisationInfoWindow);
	const toggleEditAssetForm = () => setIsEditAssetForm(!isEditAssetForm);

	const openEditMenu = () => {
		setIsEditLinkMode(true);
		toggleShowMenu();
	};

	const openEditLinkConfirm = () => setIsEditConfirm(true);

	const confirmEditLinkHandler = () => {
		openEditMenu();
		setIsEditConfirm(false);
	};

	const closeEditConfirm = () => setIsEditConfirm(false);

	const {
		unlinkedSites,
		linkSiteHandler,
		unlinkSiteHandler,
		updateAndLinkSiteHandler,
		updateAndRelinkSiteHandler,
		relinkSiteHandler,
	} = useLinkAndVerifySites({
		closeUnlinkConfirm,
		toggleUpdateSiteModal,
		closeUpdateSiteModal,
		closeLinkModal: closeLinkMenu,
		setIsEditLinkMode,
		isLinkAccess,
	});

	const { profile: siteProfile } = useSiteProfile({ id: lastLinkedSite?.id });

	useEffect(() => {
		clearLinkState();
		setLinkedSite(null);
	}, [asset.id]);

	useEffect(() => {
		if (!isEmpty(siteProfile) && !isEmpty(lastLinkedSite)) {
			const site = optionsFormattedForSites([siteProfile])[0];
			setLinkedSite(site);
			setSelectedSiteId(site);
		} else {
			setLinkedSite(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lastLinkedSite, siteProfile.id, isLinkMenu, asset.id]);

	const {
		handleUpdateAsset,
		isLoadingUpdateAsset: isUpdatingAsset,
	} = useMutateUpdateAsset(asset.id, [toggleEditAssetForm]);

	const redirectToAssetProfile = () => !isRented
		? navigate(`${adminPanel.index}${adminPanel.assets}/asset-profile/${asset.id}`)
		: navigate(`${adminPanel.index}${adminPanel.manageAcquiredAssets}/asset-profile/${asset.id}`);

	const toggleExtraInfoModalContent = useCallback((paramName: string) => {
		setIsExtraInfoOpen(!isExtraInfoOpen);
		const assetExtraInfo = getAssetExtraInfo(paramName, asset);
		setExtraInfo(assetExtraInfo);
	}, [asset, isExtraInfoOpen]);

	const closeExtraInfoModal = () => {
		setExtraInfo(null);
		setIsExtraInfoOpen(!isExtraInfoOpen);
	};

	const toggleCollapsibleBlock = (name: string) => {
		return toggleCollapsibleBlockMapInfo({
			name,
			setCollapsibleBlocks,
			collapsibleBlocks,
			initialState: collapsibleBlocksInitialState,
			disableOneItemOpenedAtATime: true,
		});
	};

	const toggleQueryAssetForm = () => setIsQueryAssetForm(!isQueryAssetForm);
	const toggleAcquireAssetForm = () => setIsAcquireAssetForm(!isAcquireAssetForm);

	const onChangeSelectedSiteId = (option: TSelectOption<string>) => setSelectedSiteId(option);

	const linkHandler = useCallback(() => {
		if (!selectedSiteId?.value) return;

		linkSiteHandler({ assetDbId: asset.id, siteDbId: selectedSiteId.value });
	}, [
		asset?.id,
		linkSiteHandler,
		selectedSiteId?.value,
	]);

	const changeLinkHandler = useCallback(() => {
		if (lastLinkedSite && selectedSiteId?.value) {
			const data = {
				assetDbId: asset.id,
				siteDbId: lastLinkedSite.id,
				newSiteId: selectedSiteId.value,
			};

			relinkSiteHandler(data);
		}
	}, [
		asset?.id,
		lastLinkedSite,
		relinkSiteHandler,
		selectedSiteId?.value,
	]);

	const rewriteHandler = useCallback(() => {
		if (selectedSiteId?.value) {
			updateAndLinkSiteHandler({ assetDbId: asset.id, siteDbId: selectedSiteId?.value });
		}
	}, [
		asset?.id,
		selectedSiteId?.value,
		updateAndLinkSiteHandler,
	]);

	const rewriteAndRelinkHandler = useCallback(() => {
		if (lastLinkedSite && selectedSiteId?.value) {
			updateAndRelinkSiteHandler({
				assetDbId: asset.id,
				siteDbId: lastLinkedSite.id,
				newSiteId: selectedSiteId.value,
			});
		}
	}, [
		asset?.id,
		lastLinkedSite,
		selectedSiteId?.value,
		updateAndRelinkSiteHandler,
	]);

	const unlinkHandler = () => {
		if (!lastLinkedSite) return;

		unlinkSiteHandler(lastLinkedSite.id);
		setIsEditLinkMode(false);
		setSelectedSiteId(null);
	};

	const powerSupplyClassname = cn(styles.statusColor, styles[asset.powerSupplyAvailabilityStatus?.name || 'RED']);
	const backhaulClassname = cn(styles.statusColor, styles[asset.backhaulAccessibilityStatus?.name || 'AMBER']);
	const availabilityClassname = cn(styles.statusColor, styles[asset.assetSiteAvailabilityStatus?.name || 'GREEN']);

	const address = [
		asset?.adPropertyNoName,
		asset?.adStreetName,
		asset?.adLocality,
		asset?.adTownCity,
		asset?.adPostCode,
	].filter(item => !!item).join(', ');

	const handlePanToView = () => {
		const { lat, lng } = asset;

		panTo({ lat, lng }, DEFAULT_CLUSTER_ZOOM_LVL);
	};

	const positiveLinkHandler = useCallback(() => {
		if (isEditLinkMode) {
			changeLinkHandler();
		} else linkHandler();
	}, [
		changeLinkHandler,
		isEditLinkMode,
		linkHandler,
	]);

	const rewriteCoords = useCallback(() => {
		if (isEditLinkMode) {
			rewriteAndRelinkHandler();
		} else rewriteHandler();
	}, [
		isEditLinkMode,
		rewriteAndRelinkHandler,
		rewriteHandler,
	]);

	return (
		<div className={styles.container}>
			<div className={styles.assetType}>
				{assetType && (
					<div className={styles.type}>
						<div>{assetType.assetTypeName}</div>
						<div className={styles.editActions}>
							{(isViewAssetOnAdminPanelAvailable || isViewAssetOnAdminForRentedAssets) && (
								<DynamicIconWrapper
									icon={Icons.viewIcon}
									onClick={redirectToAssetProfile}
									tooltipLabel='View in Admin Panel'
								/>
							)}
							{isEditAssetAvailable && (
								<DynamicIconWrapper
									icon={Icons.editIcon}
									onClick={toggleEditAssetForm}
									tooltipLabel='Edit asset on a map'
								/>
							)}
						</div>
						{(isAcquiredLabelAvailable && isRented) &&
							<div className={styles.rentStatus}>
								Acquired
							</div>
						}
						<DynamicIconWrapper
							icon={Icons.verifyCoordinates}
							onClick={handlePanToView}
							tooltipLabel='Locate asset on a map'
							withHover
						/>
					</div>
				)}
				{asset.assetSubType && (
					<div className={styles.subType}>
						{asset.assetSubType.assetSubTypeName}
					</div>
				)}
			</div>
			<div className={styles.baseInfo}>
				<div className={styles.info}>
					{!isAssetOwnerFieldHidden && (
						<div className={styles.ownerInfo}>
							<div>
								<span>Asset Owner: </span>
								{asset.owner.name || 'N/A'}
							</div>
							<Tooltip content={<span>Click to see details</span>}>
								<div
									className={cn(commonStyles.infoIcon, {
										[commonStyles.activeIcon]: isOrganisationInfoWindow,
									})}
									onClick={toggleOrganisationInfo}
								>
									<DynamicIconWrapper icon={Icons.info} />
								</div>
							</Tooltip>
						</div>
					)}
					<div>
						<span>Asset ID: </span>
						{asset.assetId || 'N/A'}
					</div>
				</div>
				<div className={styles.info}>
					<div>
						<span>NGR: </span>
						{asset.ngr || 'N/A'}
					</div>
					<div>
						<span>Latitude: </span>
						{asset.lat || 'N/A'}
					</div>
					<div>
						<span>Longitude: </span>
						{asset.lng || 'N/A'}
					</div>
				</div>
			</div>
			{areAssetFormsAvailable && (
				<div className={styles.actions}>
					<Button
						disabled={isLoading}
						fullWidth
						onClick={toggleQueryAssetForm}
						text='Query asset'
					/>
					<Button
						disabled={isLoading}
						fullWidth
						onClick={toggleAcquireAssetForm}
						text='Acquire asset'
					/>
				</div>
			)}
			<CollapsibleBlock
				isOpen={collapsibleBlocks.assetAttributes}
				label={<div className={styles.collapsibleLabel}>Asset Attributes</div>}
				name='assetAttributes'
				toggleState={toggleCollapsibleBlock}
				withSeparatorLine
			>
				<div className={styles.info}>
					<Parameter
						name='Height'
						value={!!asset.height && `${asset.height} meters`}
					/>
					<Parameter
						name='UPRN'
						value={asset.uprn}
					/>
					<Parameter
						isPrice
						name='Pricing'
						value={!!asset.indicativeAcceptablePricing
							&& `£${asset.indicativeAcceptablePricing.toFixed(2)}`
						}
					/>
					<Parameter
						handleClickInfoIcon={toggleExtraInfoModalContent}
						isExtraInfoOpened={extraInfo?.title === EParamTypesExtraInfo.siteAccessAvailability}
						name={EParamTypesExtraInfo.siteAccessAvailability}
						value={asset.isAvailable ? 'yes' : 'no'}
						withInfoIcon={!!asset.assetAccessDetails || !!asset?.assetAccessDetailsFileIds?.length}
					/>
					<Parameter
						handleClickInfoIcon={toggleExtraInfoModalContent}
						isExtraInfoOpened={extraInfo?.title === EParamTypesExtraInfo.powerSupplyAvailabilityStatus}
						isStatus
						name={EParamTypesExtraInfo.powerSupplyAvailabilityStatus}
						statusClassname={powerSupplyClassname}
						withInfoIcon={
							!!asset.powerSupplyAvailabilityDetails ||
							!!asset?.powerSupplyAvailabilityDetailsFileIds?.length
						}
					/>
					<Parameter
						handleClickInfoIcon={toggleExtraInfoModalContent}
						isExtraInfoOpened={extraInfo?.title === EParamTypesExtraInfo.backhaulA11yStatus}
						isStatus
						name={EParamTypesExtraInfo.backhaulA11yStatus}
						statusClassname={backhaulClassname}
						withInfoIcon={
							!!asset.backhaulAccessibilityDetails ||
							!!asset?.backhaulAccessibilityDetailsFileIds?.length
						}
					/>
					<Parameter
						handleClickInfoIcon={toggleExtraInfoModalContent}
						isExtraInfoOpened={extraInfo?.title === EParamTypesExtraInfo.siteAvailabilityStatus}
						isStatus
						name={EParamTypesExtraInfo.siteAvailabilityStatus}
						statusClassname={availabilityClassname}
						withInfoIcon={
							!!asset.assetSiteAvailabilityDetails ||
							!!asset?.assetSiteAvailabilityDetailsFileIds?.length
						}
					/>
					<Parameter
						handleClickInfoIcon={toggleExtraInfoModalContent}
						isExtraInfoOpened={extraInfo?.title === EParamTypesExtraInfo.specialImplementationExistence}
						name={EParamTypesExtraInfo.specialImplementationExistence}
						value={asset.specialImplementationIssuesExist ? 'yes' : 'no'}
						withInfoIcon={
							!!asset.specialImplementationIssueDetails ||
							!!asset?.specialImplementationIssueDetailsFileIds?.length
						}
					/>
					<Parameter
						name='Site Availability Review Date'
						value={asset.siteAvailabilityReviewDate && formatDate(asset.siteAvailabilityReviewDate)}
					/>
					<Parameter
						name='Responsible (LA/PFI)'
						value={asset.responsible?.name}
					/>
					<div className={cn(styles.baseInfo, styles.assetOwnerNotes)}>
						<span>Asset Owner Notes</span>
						<TextClamp text={asset.assetOwnerNotes || 'N/A'} />
					</div>
				</div>
			</CollapsibleBlock>
			{isMNOInfoAvailable && isRented && (
				<CollapsibleBlock
					isOpen={collapsibleBlocks.assetRentInfo}
					label={<div className={styles.collapsibleLabel}>Asset rent info</div>}
					name='assetRentInfo'
					toggleState={toggleCollapsibleBlock}
					withSeparatorLine
				>
					<div className={styles.info}>
						<Parameter
							name='Status'
							value={asset?.rentStatus.title}
						/>
					</div>
				</CollapsibleBlock>
			)}
			{isMNOInfoAvailable && isRented && !isAssetConsumer && (
				<CollapsibleBlock
					isOpen={collapsibleBlocks.telecomSiteInfo}
					label={<div className={styles.collapsibleLabel}>Telecom site info</div>}
					name='telecomSiteInfo'
					toggleState={toggleCollapsibleBlock}
					withSeparatorLine
				>
					<div className={styles.baseInfo}>
						<div className={styles.telecomSiteTitle}>
							Telecom Site Id
							{!isLinkMenu
								? <Actions
									editHandler={openEditLinkConfirm}
									isEdit={isEditLinkAccess && isLinked}
									isLink={isLinkAccess}
									linkHandler={isLinked ? openUnlinkConfirm : toggleShowMenu}
									linkTooltip={isLinked ? unlinkTooltipText : linkTooltipText}
									linkedToSite={isLinked}
								/>
								: <AcceptDeclineIconButtons
									negativeHandler={toggleShowMenu}
									positiveHandler={positiveLinkHandler}
								/>}
						</div>
						{isLinkMenu
							? <div className={styles.selectWrapper}>
								<Select
									handleChange={onChangeSelectedSiteId}
									isWithoutUseForms
									maxMenuHeight={120}
									menuPlacement='bottom'
									menuPortalTarget={document.body}
									name='unlinked-sites'
									options={unlinkedSites}
									placeholder='Choose your Site ID'
									value={selectedSiteId}
									width='240px'
								/>
							</div>
							: linkedSite &&
							<div className={styles.linkedSiteId}>
								{linkedSite.label} - {linkedSite?.subText}
							</div>
						}
					</div>
				</CollapsibleBlock>
			)}
			{isAddressAvailable && (
				<CollapsibleBlock
					isOpen={collapsibleBlocks.addressInfo}
					label={<div className={styles.collapsibleLabel}>Address information</div>}
					name='addressInfo'
					toggleState={toggleCollapsibleBlock}
					withSeparatorLine
				>
					<div className={styles.info}>
						{asset.adTownCity && (
							<div>
								<span>Address</span>
								<div>{address}</div>
							</div>
						)}
						{asset.locationDescription && (
							<div>
								<span>Location Description</span>
								<TextClamp text={asset.locationDescription} />
							</div>
						)}
					</div>
				</CollapsibleBlock>
			)}
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isQueryFormDirty}
				isOpen={isQueryAssetForm}
				onClose={toggleQueryAssetForm}
				title='Query asset'
				width={MODAL_WIDTH_580}
			>
				<QueryAssetForm
					closeModal={() => setIsQueryAssetForm(false)}
					setIsDirtyForm={setIsQueryFormDirty}
				/>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isAcquireFormDirty}
				isOpen={isAcquireAssetForm}
				onClose={toggleAcquireAssetForm}
				title='Acquire asset'
				width={MODAL_WIDTH_580}
			>
				<AcquireAssetForm
					closeModal={() => setIsAcquireAssetForm(false)}
					setIsDirtyForm={setIsAcquireFormDirty}
				/>
			</Modal>
			{extraInfo && (
				<Modal
					isOpen={isExtraInfoOpen}
					isScrollableContent
					onClose={closeExtraInfoModal}
					title={extraInfo.title || ''}
					width='600px'
				>
					<AssetParamInfo
						files={extraInfo?.files}
						info={extraInfo?.info}
					/>
				</Modal>
			)}
			{asset.owner?.id && (
				<Modal
					isOpen={isOrganisationInfoWindow}
					isScrollableContent
					onClose={toggleOrganisationInfo}
					title=''
					width='640px'
				>
					<OrganisationInfo id={asset.owner?.id} />
				</Modal>
			)}
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isAssetFormDirty}
				isOpen={isEditAssetForm}
				isScrollableContent
				onClose={toggleEditAssetForm}
				title='Edit asset'
				width={MODAL_WIDTH_580}
			>
				<AssetForm
					assetData={asset as unknown as TAssetProfile}
					isEditFromMap={true}
					isEditMode={true}
					isLoadingForm={isUpdatingAsset}
					setIsDirtyFormModal={setIsAssetFormDirty}
					submitHandler={handleUpdateAsset}
					toggleAssetModal={toggleEditAssetForm}
				/>
			</Modal>
			<Modal
				isOpen={isRewriteCoordConfirm}
				onClose={closeUpdateSiteModal}
			>
				<Confirm
					negativeBtnText='Cancel'
					onClose={closeUpdateSiteModal}
					onSubmit={rewriteCoords}
					positiveBtnText='Update coordinates'
					question={linkedSite?.id ? siteOutOfRadiusText : siteCantBeLinkedWarning}
				>
					{linkedSite?.id ? updateCoordsText : updateCoordsAndLinkText}
				</Confirm>
			</Modal>
			<Modal
				isOpen={isUnlinkConfirm}
				onClose={closeUnlinkConfirm}
			>
				<Confirm
					negativeBtnText='Cancel'
					onClose={closeUnlinkConfirm}
					onSubmit={unlinkHandler}
					positiveBtnText='Unlink'
					question='Are you sure you want to unlink telecom site?'
				/>
			</Modal>
			<Modal
				isOpen={isEditConfirm}
				onClose={closeEditConfirm}
			>
				<Confirm
					negativeBtnText='Cancel'
					onClose={closeEditConfirm}
					onSubmit={confirmEditLinkHandler}
					positiveBtnText='Continue'
					question='You are about to link another Site to the Asset. The current link will be disconnected. '
				>
					Please confirm your action.
				</Confirm>
			</Modal>
		</div>
	);
};

export default SingleAssetInfo;
